"use client";

import {
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  type SVGProps,
  useId,
} from "react";
import { FeedVideo } from "@/components/feed/feed-video";
import { AppImage as Image } from "@/components/ui/app-image";
import type { MessageShareMedia } from "@/db/schema/chat";
import { blurHashToDataUrl } from "@/lib/media/blurhash";
import { cn } from "@/lib/utils";
import { CHAT_AVATAR_SIZE, ChatAvatar } from "./chat-avatar";
import { shareCardMediaAspect } from "./chat-media-aspect";

type CompositeAvatarSize = "sm" | "md" | "lg";

const OVERLAY_SIZE: Record<CompositeAvatarSize, string> = {
  sm: "h-4.5 w-4.5 border",
  md: "h-5.5 w-5.5 border-2",
  lg: "h-7 w-7 border-2",
};

const OVERLAY_OFFSET: Record<CompositeAvatarSize, string> = {
  sm: "right-[-2px] top-[-2px]",
  md: "right-[-3px] top-[-3px]",
  lg: "right-[-4px] top-[-4px]",
};

/**
 * On a media tile the badge sits on the box's bottom corner instead: a 9:16
 * tile is barely wider than the badge, so a top-corner badge hid half of it.
 */
const TILE_OVERLAY_OFFSET: Record<CompositeAvatarSize, string> = {
  sm: "right-[-2px] bottom-[-2px]",
  md: "right-[-3px] bottom-[-3px]",
  lg: "right-[-4px] bottom-[-4px]",
};

const ICON_SIZE: Record<CompositeAvatarSize, string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-7 w-7",
};

const SESSION_ACCENTS = [
  "bg-sky-500/20 text-sky-100",
  "bg-emerald-500/20 text-emerald-100",
  "bg-amber-500/22 text-amber-100",
  "bg-rose-500/20 text-rose-100",
  "bg-cyan-500/20 text-cyan-100",
  "bg-fuchsia-500/20 text-fuchsia-100",
  "bg-lime-500/18 text-lime-100",
  "bg-indigo-500/20 text-indigo-100",
];

function stableIndex(seed: string, modulo: number) {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = seed.charCodeAt(index) + (hash << 5) - hash;
  }
  return Math.abs(hash) % modulo;
}

function HiiveLogoBadge({
  imageUrl,
  title,
  size,
  onTile = false,
}: {
  imageUrl: string | null | undefined;
  title: string;
  size: CompositeAvatarSize;
  onTile?: boolean;
}) {
  if (!imageUrl) return null;

  return (
    <div
      className={cn(
        "absolute z-10 overflow-hidden rounded-[6px] border border-[#0b0d12] bg-background shadow-sm",
        OVERLAY_SIZE[size],
        onTile ? TILE_OVERLAY_OFFSET[size] : OVERLAY_OFFSET[size],
      )}
    >
      <Image src={imageUrl} alt={title} fill className="object-cover" />
    </div>
  );
}

function CreatorBadge({
  imageUrl,
  title,
  size,
}: {
  imageUrl: string | null | undefined;
  title: string;
  size: CompositeAvatarSize;
}) {
  if (!imageUrl) return null;

  return (
    <div
      className={cn(
        "absolute overflow-hidden rounded-[6px] border border-[#0b0d12] bg-surface shadow-sm",
        OVERLAY_SIZE[size],
        OVERLAY_OFFSET[size],
      )}
    >
      <Image src={imageUrl} alt={title} fill className="object-cover" />
    </div>
  );
}

const TILE_RADIUS: Record<CompositeAvatarSize, string> = {
  sm: "rounded-[8px]",
  md: "rounded-[11px]",
  lg: "rounded-[14px]",
};

const TILE_SIZES: Record<CompositeAvatarSize, string> = {
  sm: "32px",
  md: "44px",
  lg: "56px",
};

/**
 * An event's banner at its own shape, fitted inside the avatar box.
 *
 * The box stays the avatar's square footprint so rows and headers keep their
 * geometry; the tile inside it takes the banner's stored aspect (a 4:5 poster
 * is a tall rectangle, a 9:16 clip a narrow one), so the media is neither
 * squeezed nor cropped to a centre square. The shape comes from stored dims
 * (else the 4:5 event upload shape), never from `onLoad`, so nothing snaps.
 *
 * A video banner PLAYS, muted, through FeedVideo: it registers with the
 * page-wide autoplay provider (one player on phones) and the proximity
 * scheduler, and PosterVideo keeps its poster up until a real frame paints.
 * `isStatic` shows the poster instead, for a second copy of a tile that is
 * already playing elsewhere on screen.
 */
export function ChatEventMediaTile({
  media,
  title,
  size = "md",
  className,
  tileClassName,
  isStatic = false,
  children,
}: {
  media: MessageShareMedia;
  title: string;
  size?: CompositeAvatarSize;
  /** The box the tile fits in; defaults to the avatar size. */
  className?: string;
  tileClassName?: string;
  isStatic?: boolean;
  /** Overlays anchored to the avatar box's corners (the hiive badge). */
  children?: ReactNode;
}) {
  const audioId = `chat-event-${useId()}`;
  const aspect = shareCardMediaAspect({ entityType: "event", media });
  const isVideo = media.kind === "video" && !isStatic;
  const stillUrl = media.kind === "video" ? media.posterUrl : media.url;
  const placeholder = blurHashToDataUrl(media.blurHash);
  // Fill the box along the banner's long side; the other follows the aspect.
  const tileStyle: CSSProperties =
    aspect >= 1
      ? { aspectRatio: aspect, width: "100%" }
      : { aspectRatio: aspect, height: "100%" };

  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center",
        CHAT_AVATAR_SIZE[size],
        className,
      )}
    >
      <div className="relative max-h-full max-w-full" style={tileStyle}>
        <div
          className={cn(
            "absolute inset-0 overflow-hidden bg-surface-alt shadow-sm",
            TILE_RADIUS[size],
            tileClassName,
          )}
        >
          {isVideo ? (
            <FeedVideo
              audioId={audioId}
              src={media.url}
              posterUrl={media.posterUrl}
              blurHash={media.blurHash}
              posterSizes={TILE_SIZES[size]}
              className="h-full w-full"
              forceMuted
            />
          ) : stillUrl ? (
            <Image
              src={stillUrl}
              alt={title}
              fill
              className="object-cover"
              sizes={TILE_SIZES[size]}
              placeholder={placeholder ? "blur" : undefined}
              blurDataURL={placeholder}
              draggable={false}
            />
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}

export function EventCompositeAvatar({
  title,
  imageUrl,
  media,
  hiiveLogoUrl,
  size = "md",
  className,
}: {
  title: string;
  imageUrl?: string | null;
  /** The banner itself; when present it renders at its own shape (and plays). */
  media?: MessageShareMedia | null;
  hiiveLogoUrl?: string | null;
  size?: CompositeAvatarSize;
  className?: string;
}) {
  if (media) {
    return (
      <ChatEventMediaTile
        media={media}
        title={title}
        size={size}
        tileClassName={className}
      >
        <HiiveLogoBadge
          imageUrl={hiiveLogoUrl}
          title={`${title} hiive`}
          size={size}
          onTile
        />
      </ChatEventMediaTile>
    );
  }

  return (
    <div className="relative shrink-0">
      <ChatAvatar
        imageUrl={imageUrl}
        fallback={title}
        size={size}
        className={className}
        containWideImage={true}
      />
      <HiiveLogoBadge
        imageUrl={hiiveLogoUrl}
        title={`${title} hiive`}
        size={size}
      />
    </div>
  );
}

export function SessionCompositeAvatar({
  title,
  activityIcon: ActivityIcon,
  creatorImageUrl,
  accentSeed,
  size = "md",
  className,
}: {
  title: string;
  activityIcon?: ComponentType<SVGProps<SVGSVGElement>> | null;
  creatorImageUrl?: string | null;
  accentSeed: string;
  size?: CompositeAvatarSize;
  className?: string;
}) {
  const accent =
    SESSION_ACCENTS[stableIndex(accentSeed, SESSION_ACCENTS.length)] ??
    SESSION_ACCENTS[0];

  return (
    <div className="relative shrink-0">
      <ChatAvatar
        imageUrl={null}
        fallback={title}
        size={size}
        className={cn(accent, className)}
        fallbackClassName="hidden"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          {ActivityIcon ? (
            <ActivityIcon
              className={cn(ICON_SIZE[size], "drop-shadow-sm")}
              aria-hidden
            />
          ) : (
            <span className="text-sm font-bold">
              {title.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      </ChatAvatar>
      <CreatorBadge
        imageUrl={creatorImageUrl}
        title={`${title} creator`}
        size={size}
      />
    </div>
  );
}
